/**
 * HistoryTab — the WIRED reservation history (change-log) tab body (SCRUM-338).
 * A `modules/booking` feature: a thin renderer over `useReservationHistory` that
 * lays each entity-change record out as an aligned grid row, decoding field
 * deltas through the SHARED, entity-agnostic `formatFieldChange` (the panel
 * already renders inside the detail's `Card` chrome, so no nested Card here) —
 * no new primitives, no barrel widening (per plan / LedgerTab precedent).
 *
 * **Design (aligned change-log grid).** The tab renders the design's grid table
 * (design_handoff_reservations/source/reservations.jsx:304-313): a bordered
 * "History of changes" header, then one row per change event laid out in aligned
 * columns — "what happened" (the semantic label, bold) · the field deltas
 * (`old → new`, old value struck through) · who (the actor) · when. Below `sm`
 * the delta and who columns collapse (design compact = what-happened + when).
 *
 * **"Who" column (SCRUM-349).** The wire `EntityChangeRecordDto` now carries a
 * resolved `actor_display_name` (the operator's display name, else their email;
 * `null` = a system / actor-less flow such as channel ingest or a hold-TTL
 * expiry). The row renders that human label, falling back to the exact copy
 * "System" when it is `null` — a raw user id NEVER reaches the screen (there is
 * no id on the wire anymore). The generic `change_kind` verb is the fallback
 * "what happened" label when `semantic_label_key` is null.
 *
 * **Deep pagination (SCRUM-338 A.5).** The hook accumulates every fetched page;
 * a kit `<Button>` "Load older" pulls the next (older) page when `hasMore`,
 * disabled while a further page is in flight, hidden otherwise.
 *
 * **Time.** `EntityChangeRecordDto.occurred_at` is **epoch-ms** (a number) — it
 * is rendered via the entity-change `formatInstant` helper (NOT `intl.formatDate`
 * on an ISO string, which the payments tab uses). The raw epoch-ms never reaches
 * the screen.
 *
 * **Honest states.** Loading skeleton; error + Retry (calls the hook's
 * `refetch`); forbidden panel (no Retry); notFound / empty `items:[]` →
 * honest-empty "No changes recorded yet" (NOT an error — no Retry).
 *
 * **A11y (WCAG 2.1 AA, FE rule 7).** Semantic `<ol>` change log, a heading, an
 * `aria-live` error region, `<time>` for each timestamp; axe-clean in light +
 * dark.
 *
 * **Tokens (ADR frontend-0002/0003, SCRUM-78/79).** Semantic `var(--token)` via
 * arbitrary-value classes only; no raw hex/rgba, no inline-style numeric
 * literals, no pixel-literal arbitrary values.
 */

import * as React from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';
import { Button } from '@sparta/ui-kit';
import { formatFieldChange } from '../../../shared/formatters/entity-change/format-field-change.js';
import { formatInstant } from '../../../shared/formatters/entity-change/values.js';
import { HISTORY_MESSAGE_IDS as H } from '../../../shared/i18n/message-ids.js';
import { useReservationHistory } from '../api/use-reservation-history.js';
import type { EntityChangeRecordDto, FieldChangeDto } from '../api/types.js';

/**
 * The generic semantic label per `change_kind`, used when a record carries no
 * `semantic_label_key` (mirrors `EntityChangeDescription`). Never a raw key on
 * screen — always a localized human verb.
 */
const GENERIC_SEMANTIC_BY_KIND: Record<EntityChangeRecordDto['change_kind'], string> = {
  created: H.semanticCreated,
  updated: H.semanticUpdated,
  deleted: H.semanticDeleted,
};

/** The human "what happened" summary for one record — the semantic label if the
 *  backend emitted one, else the generic verb for its `change_kind`. */
function semanticLabelFor(record: EntityChangeRecordDto, intl: IntlShape): string {
  if (record.semantic_label_key !== null) {
    return intl.formatMessage({
      id: record.semantic_label_key,
      defaultMessage: intl.formatMessage({ id: GENERIC_SEMANTIC_BY_KIND[record.change_kind] }),
    });
  }
  return intl.formatMessage({ id: GENERIC_SEMANTIC_BY_KIND[record.change_kind] });
}

export interface HistoryTabProps {
  /** The reservation whose history to render (`/reservations/:id`). */
  readonly reservationId: string;
}

export function HistoryTab({ reservationId }: HistoryTabProps): React.ReactElement {
  const history = useReservationHistory(reservationId);

  if (history.status === 'loading') {
    return <HistorySkeleton />;
  }
  if (history.status === 'forbidden') {
    return <HistoryForbidden />;
  }
  if (history.status === 'error') {
    return <HistoryError onRetry={history.refetch} />;
  }
  // A notFound (absent / cross-org, anti-enum) is honest-empty — NOT an error.
  if (history.status === 'notFound') {
    return <HistoryEmpty />;
  }

  // history.status === 'ready'
  const items = history.items;
  if (items.length === 0) {
    return <HistoryEmpty />;
  }
  return (
    <HistoryTimeline
      items={items}
      hasMore={history.hasMore}
      onLoadMore={history.fetchMore}
      isFetchingMore={history.isFetchingMore}
    />
  );
}

/* ── change log: an aligned grid table (design parity) ────────────────────── */

function HistoryTimeline({
  items,
  hasMore,
  onLoadMore,
  isFetchingMore,
}: {
  readonly items: readonly EntityChangeRecordDto[];
  readonly hasMore: boolean;
  readonly onLoadMore: () => void;
  readonly isFetchingMore: boolean;
}): React.ReactElement {
  return (
    <section aria-labelledby="history-heading">
      {/* Bordered header (design reservations.jsx:304-305). */}
      <div className="border-b border-[var(--border-subtle)] pb-3">
        <h3 id="history-heading" className="m-0 text-base font-bold text-[color:var(--fg-primary)]">
          History of changes
        </h3>
      </div>
      {/* Aligned change log (the design's grid, reservations.jsx:306-313): each
          row = one change event → "what happened" · field deltas (old → new) ·
          who (the resolved actor, "System" when actor-less) · when. An <ol> keeps
          the chronological-log semantics; flex tracks reproduce the design's
          aligned columns. */}
      <ol className="m-0 flex list-none flex-col p-0">
        {items.map((record) => (
          <HistoryRow key={`${record.entity_id}-${record.version}`} record={record} />
        ))}
      </ol>
      {hasMore && (
        <div className="mt-4 flex flex-col items-center gap-1">
          <Button
            variant="secondary"
            onClick={onLoadMore}
            disabled={isFetchingMore}
            // Dynamic name so the in-progress state rides the button's own label:
            // a STATIC label overrides the visible "Loading…" text, so AT would
            // only ever hear "Load older changes, dimmed" (WCAG 4.1.3).
            aria-label={isFetchingMore ? 'Loading older changes' : 'Load older changes'}
          >
            {isFetchingMore ? 'Loading…' : 'Load older'}
          </Button>
          {/* Broadcast the async load state regardless of focus — once focus
              rests on the now-disabled button a dynamic label alone is not
              reliably re-announced; a polite live region always is (the app's
              aria-live idiom). WCAG 4.1.3. */}
          <span role="status" aria-live="polite" className="sr-only">
            {isFetchingMore ? 'Loading older changes…' : ''}
          </span>
        </div>
      )}
    </section>
  );
}

/** One change-log ROW (the design's grid row): what-happened · deltas · who ·
 *  when. `occurred_at` is epoch-ms → the entity-change instant formatter (never a
 *  raw number / ISO string). The "who" cell renders the resolved
 *  `actor_display_name`, or the exact copy "System" when it is null (an
 *  actor-less flow) — never a raw user id (none is on the wire). */
function HistoryRow({ record }: { readonly record: EntityChangeRecordDto }): React.ReactElement {
  const intl = useIntl();
  const label = semanticLabelFor(record, intl);
  const actor = record.actor_display_name ?? 'System';
  const when = formatInstant(record.occurred_at, intl);
  return (
    <li className="flex items-baseline gap-3 border-t border-[var(--border-subtle)] py-3 first:border-t-0">
      {/* Col 1 — what happened (design's bold first column). On mobile it takes
          the row with the timestamp; the delta column collapses away. */}
      <span className="min-w-0 flex-1 text-sm font-semibold text-[color:var(--fg-primary)] sm:w-40 sm:flex-none sm:shrink-0">
        {label}
      </span>
      {/* Col 2 — field deltas (old → new). Hidden below sm (design compact
          collapses to what-happened + when). */}
      <span className="hidden min-w-0 flex-1 flex-col gap-0.5 text-sm text-[color:var(--fg-secondary)] sm:flex">
        {record.changes.length > 0 ? (
          record.changes.map((change, i) => (
            <FieldDeltaLine
              key={`${change.field}-${i}`}
              change={change}
              entityType={record.entity_type}
            />
          ))
        ) : (
          <span className="text-[color:var(--fg-tertiary)]">—</span>
        )}
      </span>
      {/* Col 3 — who (the actor). The resolved `actor_display_name`, or "System"
          when actor-less. Hidden below sm alongside the delta column (design
          compact collapses to what-happened + when); never a raw user id. */}
      <span className="hidden shrink-0 text-sm text-[color:var(--fg-secondary)] sm:flex sm:w-32">
        {actor}
      </span>
      {/* Col 4 — when. */}
      <time className="shrink-0 text-xs text-[color:var(--fg-tertiary)] sm:w-24 sm:text-right">
        {when}
      </time>
    </li>
  );
}

/** One field delta inside a row — `label: old → new` with the OLD value struck
 *  through (design parity). Falls back to the joined `detail` for a redacted /
 *  unmapped / structured change (no separable from/to). */
function FieldDeltaLine({
  change,
  entityType,
}: {
  readonly change: FieldChangeDto;
  readonly entityType: string;
}): React.ReactElement {
  const intl = useIntl();
  const { label, detail, fromText, toText } = formatFieldChange(change, entityType, intl, {});
  return (
    <span className="block">
      <span className="text-[color:var(--fg-primary)]">{label}: </span>
      {fromText !== null && toText !== null ? (
        <>
          <s className="text-[color:var(--fg-tertiary)]">{fromText}</s> → {toText}
        </>
      ) : fromText === null && toText !== null ? (
        <>{toText}</>
      ) : fromText !== null && toText === null ? (
        <>
          <s className="text-[color:var(--fg-tertiary)]">{fromText}</s> → —
        </>
      ) : (
        <>{detail}</>
      )}
    </span>
  );
}

/* ── loading skeleton ─────────────────────────────────────────────────────── */

function HistorySkeleton(): React.ReactElement {
  return (
    <div aria-busy="true" aria-live="polite" data-testid="skeleton">
      <span className="sr-only">Loading history…</span>
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="inline-block h-5 w-full rounded bg-[var(--bg-sunken)]"
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
}

/* ── error + retry ────────────────────────────────────────────────────────── */

function HistoryError({ onRetry }: { readonly onRetry: () => void }): React.ReactElement {
  return (
    <div role="alert" aria-live="assertive" className="flex items-center justify-between gap-4">
      <div>
        <h3 className="mb-1 text-base font-semibold text-[color:var(--fg-primary)]">
          Couldn’t load the history.
        </h3>
        <p className="text-sm text-[color:var(--fg-secondary)]">
          Check your connection and try again.
        </p>
      </div>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}

/* ── forbidden panel (no Retry) ───────────────────────────────────────────── */

function HistoryForbidden(): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <h3 className="text-sm font-semibold text-[color:var(--fg-primary)]">
        History needs additional access
      </h3>
      <p className="mt-1 max-w-sm text-sm text-[color:var(--fg-secondary)]">
        Your role can’t view this reservation’s change history yet.
      </p>
    </div>
  );
}

/* ── honest-empty: notFound / no changes recorded ─────────────────────────── */

function HistoryEmpty(): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <h3 className="text-sm font-semibold text-[color:var(--fg-primary)]">
        No changes recorded yet
      </h3>
      <p className="mt-1 max-w-sm text-sm text-[color:var(--fg-secondary)]">
        Changes to this reservation will appear here as they happen.
      </p>
    </div>
  );
}
